// Talent sub-modules.
import location from './talent/location'
import profile from './talent/profile'
import tag from './talent/tag'
import opportunity from './talent/opportunity'
import endorsement from './talent/endorsement'
import packages from './talent/packages'
import note from './talent/note'
import { http, notify } from 'utils'
import { checkEmail } from 'utils/errorHelpers'
import { pick, concat } from 'lodash'

// Talent mutation imports.
import { SET_TALENTS, SET_TALENTS_COUNTS, SET_TALENTS_PAGINATION, ADD_TALENTS, RESET_TALENTS } from 'vx/mutations'

const modules = {
  location,
  profile,
  tag,
  opportunity,
  endorsement,
  packages,
  note
}

// Talent state.
const state = {
  talent: null,
  talents: [],
  talentsCounts: {
    all: 0,
    active: 0,
    inactive: 0
  },
  pagination: {}
}

// Talent state getters.
const getters = {
  /**
   * Determine if we have a single talent.
   *
   * @return bool
   */
  haveTalent: (state) => {
    return state.booking !== null &&
      state.talent !== null
  },

  /**
   * Determine if we have no more talent to load.
   *
   * @return bool
   */
  haveNoMoreTalents: (state) => {
    return state.pagination && (state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0)
  }
}

// Talent actions
const actions = {
  getTalentsCounts ({ commit }, params = {}) {
    return http.get('talent-counts', { params })
      .then((response) => {
        commit(SET_TALENTS_COUNTS, response.data)

        return response
      })
  },

  /**
   * Get a booking PDF.
   * The audience and userId are for when an admin is retrieving pdf acting as the audience.
   *
   * @return Promise
   */
  getPdf (store, { id }) {
    return http.get(`users/${id}/talent_profile_pdf`)
  },

  getTalents ({ commit }, params = {}) {
    commit(RESET_TALENTS)

    return http.get('talent', { params })
      .then((response) => {
        commit(SET_TALENTS, response.data.data)
        commit(SET_TALENTS_PAGINATION, response.data.meta)
        return response
      })
  },
  /**
   * Get next set of paginated talents.
   *
   * @return Promise
   */
  getNextTalents ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreTalents) {
      return Promise.reject(new Error('There are no more talents to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`talent`, { params })
      .then((response) => {
        commit(ADD_TALENTS, response.data.data)
        commit(SET_TALENTS_PAGINATION, response.data.meta)

        return response
      })
  },

  storeTalent (store, payload) {
    return http.post(`talent`, payload)
      .catch(error => {
        if (checkEmail(error)) {
          notify.error('That email has already been taken.')
        } else {
          notify.error('Something went wrong. Please check your input and try again.')
        }
      })
  },

  resetTalents ({ commit }) {
    commit(RESET_TALENTS)
  }
}

// Talent state mutations.
const mutations = {

  [SET_TALENTS] (state, talents) {
    state.talents = talents
  },

  [SET_TALENTS_COUNTS] (state, talentsCounts) {
    state.talentsCounts = talentsCounts
  },

  [SET_TALENTS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_TALENTS] (state, talents) {
    state.talents = concat(state.talents, talents)
  },

  [RESET_TALENTS] (state, talents) {
    state.talents = []
    state.pagination = {}
  },

  [RESET_TALENTS] (state, pagination) {
    state.talents =
      state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}
